import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../patterns';
import { SoilRound } from '../../services/nutritionService';
import NutritionMembershipPicker, { flattenOrchards } from './NutritionMembershipPicker';
import './nutrition.css';

export interface NutritionConvertRoundModalProps {
  show: boolean;
  round: SoilRound | null;
  onConfirm: (ids: string[]) => void;
  onCancel: () => void;
  submitting?: boolean;
  error?: string | null;
  /**
   * orchardNos (foliar) / groupIds (soil) that already have a non-deleted
   * session for this round. They are hidden from the list and excluded from
   * the default selection — only deleting the existing session frees them.
   */
  unavailableIds?: string[];
}

// ─── Main component ───────────────────────────────────────────────────────────

const NutritionConvertRoundModal: React.FC<NutritionConvertRoundModalProps> = ({
  show,
  round,
  onConfirm,
  onCancel,
  submitting = false,
  error = null,
  unavailableIds,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection whenever the modal opens with a new round, or once the
  // set of already-converted (unavailable) ids resolves. Seeds with the
  // AVAILABLE items only — orchards/groups that already have a session are
  // never pre-selected.
  useEffect(() => {
    if (show && round) {
      const taken = new Set(unavailableIds || []);
      if (round.type === 'soil') {
        setSelected(
          new Set((round.groups || []).map((g) => g.groupId).filter((id) => !taken.has(id)))
        );
      } else {
        setSelected(new Set(flattenOrchards(round).filter((id) => !taken.has(id))));
      }
    }
  }, [show, round, unavailableIds]);

  if (!round) return null;

  const isSoil = round.type === 'soil';
  const unavailable = new Set(unavailableIds || []);

  // Full lists from the round definition, then filter out orchards/groups
  // that already have a non-deleted session for this round.
  const orchards = isSoil ? [] : flattenOrchards(round).filter((o) => !unavailable.has(o));
  const groups = isSoil
    ? (round.groups || []).filter((g) => !unavailable.has(g.groupId))
    : [];
  const items: string[] = isSoil
    ? groups.map((g) => g.groupId)
    : orchards;

  // Count/confirm against the AVAILABLE items only, so a hidden (already-
  // converted) id can never leak into the selection even if `selected` still
  // holds it from an earlier seed.
  const selectedItems = items.filter((id) => selected.has(id));

  const handleConfirm = () => {
    if (submitting) return;
    onConfirm(selectedItems);
  };

  const roundLabel = round.name || t('nutrition.sessions.convertModal.defaultRoundName');

  const description = isSoil
    ? t('nutrition.sessions.convertModal.soilDescription')
    : t('nutrition.sessions.convertModal.description');

  const confirmLabel = submitting
    ? t('nutrition.sessions.convertModal.converting')
    : isSoil
    ? t('nutrition.sessions.convertModal.soilConfirm', { count: selectedItems.length })
    : t('nutrition.sessions.convertModal.confirm', { count: selectedItems.length });

  return (
    <Modal
      show={show}
      onHide={onCancel}
      title={t('nutrition.sessions.convertModal.title', { round: roundLabel })}
    >
      <Modal.Body>
        {error && (
          <div className="ntr-error" role="alert">{error}</div>
        )}
        <p className="ntr-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {description}
        </p>
        <NutritionMembershipPicker
          round={round}
          unavailableIds={unavailableIds}
          value={selectedItems}
          onChange={(ids) => setSelected(new Set(ids))}
          selectAllTestId="convert-select-all"
        />
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={onCancel}
          disabled={submitting}
          data-testid="convert-round-cancel"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={handleConfirm}
          disabled={submitting || selectedItems.length === 0}
          data-testid="convert-round-confirm"
        >
          {confirmLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default NutritionConvertRoundModal;
